import React, { useState, useEffect } from 'react';
import { User, Mail, Calendar, Heart, ShoppingBag, Settings, Loader2, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const { user, isAuthenticated, token, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserData();
    }
  }, [isAuthenticated]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      // Fetch user details
      const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData({
        name: userResponse.data.name || '',
        email: userResponse.data.email || ''
      });

      // Fetch favorites count
      try {
        const favResponse = await axios.get(`${API_URL}/api/user/favorites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(favResponse.data);
      } catch (e) {
        setFavorites([]);
      }

      // Fetch orders (if available)
      try {
        const ordersResponse = await axios.get(`${API_URL}/api/user/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(ordersResponse.data);
      } catch (e) {
        setOrders([]);
      }

    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/api/user/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (updateUser) {
        updateUser({ ...user, name: formData.name });
      }
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50 flex items-center justify-center">
        <Card className="max-w-md border-4 border-teal-300">
          <CardContent className="p-8 text-center">
            <User className="h-16 w-16 text-teal-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Sign in to view your profile</h2>
            <p className="text-gray-600 mb-6">Access your account settings and saved activities</p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-gradient-to-r from-blue-500 to-teal-500 text-white">
                  Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button variant="outline" className="w-full border-2 border-teal-400">
                  Create Account
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-teal-500 mx-auto mb-4" />
          <p className="text-xl text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 flex items-center justify-center mx-auto mb-4 shadow-xl">
            <span className="text-4xl font-black text-white">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-2">{user?.name}</h1>
          <p className="text-gray-600">{user?.email}</p>
          {user?.role === 'admin' && (
            <Badge className="mt-2 bg-purple-100 text-purple-800">Admin</Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Stats Card - Favorites */}
          <Link to="/favorites">
            <Card className="border-4 border-teal-200 hover:border-teal-400 transition-all cursor-pointer hover:shadow-lg">
              <CardContent className="p-6 text-center">
                <Heart className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-3xl font-black text-gray-900">{favorites.length}</p>
                <p className="text-gray-600">Saved Activities</p>
              </CardContent>
            </Card>
          </Link>

          {/* Stats Card - Orders */}
          <Card className="border-4 border-teal-200">
            <CardContent className="p-6 text-center">
              <ShoppingBag className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-3xl font-black text-gray-900">{orders.length}</p>
              <p className="text-gray-600">Purchases</p>
            </CardContent>
          </Card>

          {/* Stats Card - Member Since */}
          <Card className="border-4 border-teal-200">
            <CardContent className="p-6 text-center">
              <Calendar className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <p className="text-3xl font-black text-gray-900">
                {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </p>
              <p className="text-gray-600">Member Since</p>
            </CardContent>
          </Card>
        </div>

        {/* Profile Settings */}
        <Card className="border-4 border-teal-300 mb-8">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-teal-500 text-white p-6 rounded-t-lg">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Settings className="h-6 w-6" />
                <h2 className="text-2xl font-bold">Profile Settings</h2>
              </div>
              {!editing ? (
                <Button 
                  variant="outline" 
                  className="bg-white/20 border-white/50 text-white hover:bg-white/30"
                  onClick={() => setEditing(true)}
                >
                  Edit Profile
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="bg-white/20 border-white/50 text-white hover:bg-white/30"
                    onClick={() => setEditing(false)}
                    disabled={saving}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Button 
                    className="bg-white text-teal-600 hover:bg-white/90"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              {editing ? (
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="border-2 border-teal-300"
                />
              ) : (
                <p className="text-lg text-gray-900 py-2">{formData.name || 'Not set'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-gray-400" />
                <p className="text-lg text-gray-900">{formData.email}</p>
              </div>
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>
          </CardContent>
        </Card>

        {/* Recent Orders */}
        {orders.length > 0 && (
          <Card className="border-4 border-teal-300">
            <CardHeader className="p-6 border-b">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <ShoppingBag className="h-6 w-6 text-teal-600" />
                Purchase History
              </h2>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {orders.map((order, index) => (
                  <div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-semibold">{order.package_name}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">${order.amount?.toFixed(2)}</p>
                      <Badge className="bg-green-100 text-green-800">{order.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          <Link to="/favorites">
            <Button variant="outline" className="w-full h-16 text-lg border-2 border-teal-300 hover:bg-teal-50">
              <Heart className="h-5 w-5 mr-2 text-red-500" />
              View Saved Activities
            </Button>
          </Link>
          <Link to="/activities">
            <Button variant="outline" className="w-full h-16 text-lg border-2 border-teal-300 hover:bg-teal-50">
              <Settings className="h-5 w-5 mr-2 text-teal-500" />
              Browse All Activities
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Profile;
